import {
  SiteReaderKind,
  type GetSiteReaderDevicesQuery,
} from 'apollo/generated/client-operations';
import { readerLabel, type ReaderDisplayFields } from './readerPresentation';

export type SiteReader = Pick<ReaderDisplayFields, 'kind' | 'health'> & {
  /** The physical reader id used by the layout's local model. */
  id: string;
  /** The existing site registration used by placement commands. */
  readerId: string;
  name: string;
  portLabel: string;
  missing: boolean;
  notConnected: boolean;
};

type ListedReader = Pick<
  GetSiteReaderDevicesQuery['siteIndependentReaders'][number],
  | 'readerDeviceId'
  | 'readerId'
  | 'name'
  | 'kind'
  | 'entry'
  | 'health'
  | 'missing'
  | 'connection'
>;
const toSiteReader = (
  reader: ListedReader,
  portLabel: string,
  controllerName?: string,
  portName?: string,
): SiteReader => ({
  id: reader.readerDeviceId,
  readerId: reader.readerId,
  name: readerLabel(reader, controllerName, portName),
  kind: reader.kind,
  health: reader.health,
  missing: reader.missing,
  notConnected: !reader.connection,
  portLabel: reader.missing
    ? 'Not listed for this site in Avigilon'
    : portLabel,
});

/** M1's registered site readers, including unresolved and independent readers, deduplicated across controllers. */
export const siteReadersOf = (
  data: GetSiteReaderDevicesQuery,
): SiteReader[] => {
  const readers = data.siteDevices.flatMap((controller) =>
    controller.readers.map((reader) =>
      toSiteReader(
        reader,
        reader.port
          ? `${controller.name} · ${
              reader.port.label || `Port ${reader.port.position}`
            }`
          : 'Not on a port',
        controller.name,
        reader.port?.label ||
          (reader.port ? `Port ${reader.port.position}` : undefined),
      ),
    ),
  );
  const byDeviceId = new Map(readers.map((reader) => [reader.id, reader]));
  data.siteUnresolvedReaders.forEach((reader) => {
    if (byDeviceId.has(reader.readerDeviceId)) return;
    const portLabel = reader.controller
      ? `${reader.controller.name} · ${reader.controller.portLabel || 'Port'}`
      : 'Not on a port';
    byDeviceId.set(
      reader.readerDeviceId,
      toSiteReader(
        reader,
        portLabel,
        reader.controller?.name,
        reader.controller?.portLabel || undefined,
      ),
    );
  });
  data.siteIndependentReaders.forEach((reader) => {
    const independent = toSiteReader(reader, 'Independent reader');
    const attached = byDeviceId.get(independent.id);
    // Independent ownership does not erase a known physical port.
    if (independent.kind === SiteReaderKind.Wiegand && attached)
      independent.name = attached.name;
    byDeviceId.set(independent.id, independent);
  });
  return [...byDeviceId.values()].sort((a, b) => a.name.localeCompare(b.name));
};
